import axiosInstance from '../utils/axiosConfig';
import keepServerAnswer from '../utils/keepServerAnswer';

// Hard cap for the "vessels within range" radius. MUST match the backend cap
// (Km is clamped to 200 server-side) so the UI never lets the user ask for a
// wider search than the API will honour.
export const VESSELS_WITHIN_RANGE_MAX_KM = 200;

export const globeService = {
    /**
     * Get ports data with options for pagination and filtering.
     */
    getPortsData: async (options = {}) => {
        try {
            const { limit = 1000, offset = 0, country } = options;
            const params = new URLSearchParams();
            if (limit) params.append('limit', limit);
            if (offset) params.append('offset', offset);
            if (country) params.append('country', country);
            const url = `/api/globe/city-data/?${params.toString()}`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching ports data:', error);
            throw error;
        }
    },

    /**
     * Get all world ports (with no pagination limit).
     */
    getAllPorts: async () => {
        try {
            const response = await globeService.getPortsData({ limit: 100000 });
            return response;
        } catch (error) {
            console.error('Error fetching ports:', error);
            throw error;
        }
    },

    // ─── NavAPI Vessel Tracking ──────────────────────────────────────

    /**
     * Autocomplete vessel search by name, IMO, MMSI, or call sign.
     * @param {string} query - Min 3 characters
     * @returns {Promise<Array>} Array of vessel search results
     */
    shipSearch: async (query) => {
        try {
            if (!query || query.trim().length < 3) return [];
            const response = await axiosInstance.get(`/api/globe/ship-search/?q=${encodeURIComponent(query.trim())}`);
            return response.data.results || [];
        } catch (error) {
            console.error('Error in ship search:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Vessel search by Name, IMO, MMSI, or call sign against the upgraded
     * ship-search endpoint (primary feed first, secondary-provider name fallback deduped by
     * IMO/MMSI server-side). Distinguishes "no matches" (a calm empty result)
     * from a real failure (throws): the backend returns {results: []} with
     * HTTP 200 for a genuine miss, so an empty array here is NOT an error.
     * @param {string} query - Min 3 characters
     * @returns {Promise<Array>} Array of vessel search results (possibly empty)
     */
    searchVessels: async (query) => {
        return globeService.shipSearch(query);
    },

    // ─── Vessel intelligence / Risk & Compliance (config-gated) ──────
    // Every endpoint below degrades gracefully: when the provider is not configured
    // the backend returns {configured: false} with HTTP 200. Callers should
    // treat configured:false as "hide the panel", never as an error.

    /**
     * Sanctions / compliance screening for a hull. Backend persists a
     * VesselComplianceSnapshot and reuses it within 6h.
     * @param {string} imo - IMO number (7 digits)
     * @returns {Promise<Object>} { configured, imo, sanctions_status,
     *   sanction_lists_matched, max_match_score, screened_at, source }
     */
    getVesselCompliance: async (imo) => {
        try {
            if (!imo) throw new Error('IMO number is required');
            const response = await axiosInstance.get(`/api/globe/vessel-compliance/?imo=${encodeURIComponent(imo.trim())}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching vessel compliance:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Behavioural risk signals (dark activity, STS, flag risk, zone visits).
     * @param {string} imo - IMO number (7 digits)
     * @returns {Promise<Object>} { configured, data: { imo, dark_activity,
     *   sts, flag_risk, zone_visits, source } }
     */
    getVesselRisk: async (imo) => {
        try {
            if (!imo) throw new Error('IMO number is required');
            const response = await axiosInstance.get(`/api/globe/vessel-risk/?imo=${encodeURIComponent(imo.trim())}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching vessel risk:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Predicted destination + ETA (with alternatives). Not cached server-side.
     * @param {string} imo - IMO number (7 digits)
     * @returns {Promise<Object>} { configured, data: { imo,
     *   predicted_destination, predicted_eta, alternatives: [] } }
     */
    getVesselSmartEta: async (imo) => {
        try {
            if (!imo) throw new Error('IMO number is required');
            const response = await axiosInstance.get(`/api/globe/vessel-smart-eta/?imo=${encodeURIComponent(imo.trim())}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching vessel smart ETA:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Recent port calls for a hull.
     * @param {string} imo - IMO number (7 digits)
     * @param {number} limit - Default 20, clamped 1..100 server-side
     * @returns {Promise<Object>} { configured, data: [{ port, country,
     *   arrival, departure }] }
     */
    getVesselPortCalls: async (imo, limit = 20) => {
        try {
            if (!imo) throw new Error('IMO number is required');
            const params = new URLSearchParams();
            params.append('imo', imo.trim());
            if (limit) params.append('limit', limit);
            const response = await axiosInstance.get(`/api/globe/vessel-port-calls/?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching vessel port calls:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Vessel particulars for delivery auto-fill. Prefers the intelligence
     * provider; falls back to the primary ship-details feed (reshaped) so this
     * still returns data with configured:false. loa/beam/draught are null on
     * the fallback.
     * @param {string} imo - IMO number (7 digits)
     * @returns {Promise<Object>} { data: { imo, mmsi, name, ship_type, dwt,
     *   gross_tonnage, year_built, loa, beam, draught, owner, manager, flag,
     *   source }, configured }
     */
    getVesselParticulars: async (imo) => {
        try {
            if (!imo) throw new Error('IMO number is required');
            const response = await axiosInstance.get(`/api/globe/vessel-particulars/?imo=${encodeURIComponent(imo.trim())}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching vessel particulars:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Get last known position for a single vessel.
     * @param {string} imo - IMO number (7 digits)
     * @param {string} mmsi - MMSI number (9 digits)
     * @returns {Promise<Object>} Vessel position data
     */
    getVesselPosition: async (imo, mmsi) => {
        try {
            const params = new URLSearchParams();
            if (imo) params.append('imo', imo.trim());
            if (mmsi) params.append('mmsi', mmsi.trim());
            const response = await axiosInstance.get(`/api/globe/vessel-position/?${params.toString()}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching vessel position:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Batch lookup for multiple vessel positions.
     * @param {Array<string>} imoList - Array of IMO numbers
     * @param {Array<string>} mmsiList - Array of MMSI numbers
     * @returns {Promise<Array>} Array of vessel positions
     */
    getFleetPositions: async (imoList = null, mmsiList = null) => {
        try {
            const { positions } = await globeService.getFleetPositionsDetailed(imoList, mmsiList);
            return positions;
        } catch (error) {
            console.error('Error fetching fleet positions:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Batch position lookup that also surfaces how many requested vessels had
     * no real AIS fix (so the UI can list them under "Position unavailable"
     * instead of silently dropping them). The backend already filters out
     * vessels without a real lat/lon -- `positions` only ever contains
     * plottable vessels, and `noPositionCount` is the count it dropped.
     * @param {Array<string>} imoList - Array of IMO numbers
     * @param {Array<string>} mmsiList - Array of MMSI numbers
     * @returns {Promise<{positions: Array, noPositionCount: number}>}
     */
    getFleetPositionsDetailed: async (imoList = null, mmsiList = null) => {
        try {
            const body = {};
            if (imoList) body.imo_list = imoList;
            if (mmsiList) body.mmsi_list = mmsiList;
            const response = await axiosInstance.post('/api/globe/fleet-positions/', body);
            return {
                positions: response.data.data || [],
                noPositionCount: response.data.no_position_count || 0,
            };
        } catch (error) {
            console.error('Error fetching fleet positions:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Get historical track with date range (max 31 days).
     * @param {string} imo - IMO number
     * @param {string} mmsi - MMSI number
     * @param {string} dateFrom - ISO 8601 start date
     * @param {string} dateUntil - ISO 8601 end date
     * @returns {Promise<Object>} { vessel, positions }
     */
    getVesselHistory: async (imo, mmsi, dateFrom, dateUntil) => {
        try {
            const body = { date_from: dateFrom, date_until: dateUntil };
            if (imo) body.imo = imo;
            if (mmsi) body.mmsi = mmsi;
            const response = await axiosInstance.post('/api/globe/vessel-history/', body);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching vessel history:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Navigable port-to-port sea route (follows real shipping lanes around land).
     * Pass loading/discharge coordinates (or port codes). Returns
     * { configured, data: { waypoints: [[lat,lon],...], total_distance, seca_distance } }.
     * Config-gated + cached server-side; configured:false means routing isn't enabled.
     */
    getSeaRoute: async ({ startLat, startLon, endLat, endLon, startPort, endPort, secaAvoidance } = {}) => {
        try {
            const params = new URLSearchParams();
            if (startLat != null) params.append('start_lat', startLat);
            if (startLon != null) params.append('start_lon', startLon);
            if (endLat != null) params.append('end_lat', endLat);
            if (endLon != null) params.append('end_lon', endLon);
            if (startPort) params.append('start_port', startPort);
            if (endPort) params.append('end_port', endPort);
            if (secaAvoidance) params.append('seca_avoidance', '1');
            const response = await axiosInstance.get(`/api/globe/sea-route/?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching sea route:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Batch navigable sea routes (MultipleRoutes) — one round-trip for many legs.
     * Each leg is either {startPortCode, endPortCode} or
     * {startLat, startLon, endLat, endLon}. Returns
     * { configured, count, data: [{waypoints:[[lat,lon],...], total_distance,
     * seca_distance, result_text}] } in the SAME leg order that was sent.
     * TOKEN-EXPENSIVE (1 token/leg, capped ≤16 legs) but cached 30 days
     * server-side. configured:false ⇒ routing not enabled (hide the line).
     * @param {Array<Object>} legs
     * @param {boolean} secaAvoidance - route around SECA zones (default false)
     */
    getSeaRoutesBatch: async (legs = [], secaAvoidance = false) => {
        try {
            const cleanLegs = (legs || []).map((leg) => {
                const out = {};
                if (leg.startPortCode) out.start_port_code = leg.startPortCode;
                if (leg.endPortCode) out.end_port_code = leg.endPortCode;
                if (leg.startLat != null) out.start_lat = leg.startLat;
                if (leg.startLon != null) out.start_lon = leg.startLon;
                if (leg.endLat != null) out.end_lat = leg.endLat;
                if (leg.endLon != null) out.end_lon = leg.endLon;
                return out;
            });
            const response = await axiosInstance.post('/api/globe/sea-routes-batch/', {
                legs: cleanLegs,
                seca_avoidance: !!secaAvoidance,
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching batch sea routes:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * SECA / non-SECA partition of an already-computed route (FREE — no token).
     * Pass the route's waypoints as Leaflet [lat,lon] pairs (≥2). Returns
     * { configured, total_distance, seca_distance, segments:
     * [{waypoints:[[lat,lon],...], total_distance, seca_distance, is_seca}] }.
     * Cached 30 days server-side. configured:false ⇒ leave the route mono-colour.
     * @param {Array<Array<number>>} waypoints - [[lat,lon],...]
     */
    getSecaSegments: async (waypoints = []) => {
        try {
            const response = await axiosInstance.post('/api/globe/seca-segments/', {
                waypoints: waypoints || [],
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching SECA segments:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Bunker-detour comparison (RoutesVia): direct A→B route vs the best route
     * that passes through one of the supplied bunker midpoints.
     * TOKEN-EXPENSIVE (1 token per computed leg) → call ONLY on explicit user
     * action, never automatically. Origin/destination/midpoints each accept
     * {lat,lon} or {portCode}. Returns
     * { configured, base: {waypoints, total_distance, seca_distance},
     *   via: {waypoints, total_distance, seca_distance, deviation_distance,
     *   seca_delta} }. Waypoints are Leaflet [lat,lon]. Cached 30 days.
     * @param {Object} origin - {lat,lon} or {portCode}
     * @param {Object} destination - {lat,lon} or {portCode}
     * @param {Array<Object>} midpoints - bunker candidates (non-empty, ≤8)
     */
    getRoutesVia: async (origin, destination, midpoints = []) => {
        const asPoint = (p) => {
            if (!p) return null;
            if (p.lat != null && p.lon != null) return { lat: p.lat, lon: p.lon };
            if (p.portCode) return { port_code: p.portCode };
            if (p.port_code) return { port_code: p.port_code };
            return null;
        };
        try {
            const body = {
                origin: asPoint(origin),
                destination: asPoint(destination),
                midpoints: (midpoints || []).map(asPoint).filter(Boolean),
            };
            const response = await axiosInstance.post('/api/globe/routes-via/', body);
            return response.data;
        } catch (error) {
            console.error('Error fetching routes-via:', error);
            throw _normalizeError(error);
        }
    },

    // ─── AIS vessel discovery (TOKEN-EXPENSIVE) ──────────────────────
    // Both endpoints below bill ~1 token per vessel RETURNED — a busy port can
    // be hundreds. NEVER call these automatically: only on an explicit user
    // "Search" action, and always surface the token cost in the UI. Both return
    // HTTP 200 with {configured:false, data:[], count:0} when the AIS key is
    // unset, so configured:false means "hide the feature", never an error.

    /**
     * Vessels currently WITHIN a radius of a centre point (AIS WithinRange).
     * TOKEN-EXPENSIVE (~1 token/vessel returned). Centre is a lat/lon; radius
     * is in km (clamped to VESSELS_WITHIN_RANGE_MAX_KM = 200 server-side).
     * @param {Object} opts
     * @param {number} opts.lat - centre latitude (-90..90, required)
     * @param {number} opts.lon - centre longitude (-180..180, required)
     * @param {number} [opts.km=50] - radius in km (capped at 200)
     * @param {string} [opts.shipType] - comma list of AIS ship-type codes
     * @param {string} [opts.navStatus] - comma list of nav-status codes 0-15
     * @returns {Promise<Object>} { configured, count, data: [<position>...] }
     */
    getVesselsWithinRange: async ({ lat, lon, km = 50, shipType = '', navStatus = '' } = {}) => {
        try {
            if (lat == null || lon == null) throw new Error('A centre latitude and longitude are required');
            const params = new URLSearchParams();
            params.append('Lat', lat);
            params.append('Lon', lon);
            if (km != null) params.append('Km', Math.min(Number(km) || 50, VESSELS_WITHIN_RANGE_MAX_KM));
            if (shipType) params.append('ShipType', shipType);
            if (navStatus) params.append('NavStatus', navStatus);
            const response = await axiosInstance.get(`/api/globe/vessels-within-range/?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching vessels within range:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Vessels currently heading to one or more DESTINATION ports, optionally
     * bounded by an ETA window (AIS ByDestination). TOKEN-EXPENSIVE
     * (~1 token/vessel returned). Destinations can be UN/LOCODEs or free-text
     * names; the backend resolves them.
     * @param {Object} opts
     * @param {Array<string>} opts.destinations - non-empty, ≤50 ports
     * @param {string} [opts.etaFrom] - ISO 'YYYY-MM-DDTHH:MM:SSZ'
     * @param {string} [opts.etaUntil] - ISO; after etaFrom, window ≤90 days
     * @param {string} [opts.shipType] - comma list of AIS ship-type codes
     * @returns {Promise<Object>} { configured, count, data: [<position>...] }
     */
    getVesselsByDestination: async ({ destinations = [], etaFrom = '', etaUntil = '', shipType = '' } = {}) => {
        try {
            const dests = (destinations || []).map(d => (d || '').trim()).filter(Boolean);
            if (dests.length === 0) throw new Error('At least one destination port is required');
            const body = { destinations: dests };
            if (etaFrom) body.eta_from = etaFrom;
            if (etaUntil) body.eta_until = etaUntil;
            if (shipType) body.ship_type = shipType;
            const response = await axiosInstance.post('/api/globe/vessels-by-destination/', body);
            return response.data;
        } catch (error) {
            console.error('Error fetching vessels by destination:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Resolve a free-text bunker/port NAME (or UN/LOCODE) to navigable coords
     * via the shared local-DB → SeaPortSearch → geocoding tier. Returns
     * { lat, lon, name, code, ... } or throws on a genuine miss (404).
     * @param {string} query - port name or code (min 3 chars)
     */
    resolvePort: async (query) => {
        try {
            const q = (query || '').trim();
            if (q.length < 3) throw new Error('Enter at least 3 characters');
            const response = await axiosInstance.get(`/api/globe/resolve-port/?q=${encodeURIComponent(q)}`);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                throw keepServerAnswer(error, `Could not find a port matching "${query}"`);
            }
            console.error('Error resolving port:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Live port autocomplete for the delivery-port picker (SeaPortSearch, which
     * bills 0.01 routing tokens per search — 100 searches = 1 token; interactive
     * use only).
     * GET /api/globe/port-search/?q=<query>. Each result is an alias-split option
     * carrying { label, port_code, port_name, latitude, longitude, country } so a
     * selection is NavAPI-canonical (correct coords + code). A query under 3 chars
     * or an unconfigured routing key both yield an empty list — NOT an error.
     * @param {string} query - Min 3 characters
     * @returns {Promise<{configured: boolean, results: Array}>}
     */
    portSearch: async (query) => {
        try {
            const q = (query || '').trim();
            if (q.length < 3) return { configured: true, results: [] };
            const response = await axiosInstance.get(`/api/globe/port-search/?q=${encodeURIComponent(q)}`);
            return {
                configured: response.data?.configured !== false,
                results: response.data?.results || [],
            };
        } catch (error) {
            console.error('Error in port search:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Get vessel specifications and ownership from Ships Database.
     * @param {string} imo - IMO number (7 digits)
     * @returns {Promise<Object>} Vessel details
     */
    getVesselDetails: async (imo) => {
        try {
            if (!imo) throw new Error('IMO number is required');
            const response = await axiosInstance.get(`/api/globe/vessel-details/?imo=${imo.trim()}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching vessel details:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Combined endpoint: position + details in one call.
     * @param {string} imo - IMO number
     * @param {string} mmsi - MMSI number (optional)
     * @returns {Promise<Object>} { position, details }
     */
    getVesselFullInfo: async (imo, mmsi) => {
        try {
            const params = new URLSearchParams();
            if (imo) params.append('imo', imo.trim());
            if (mmsi) params.append('mmsi', mmsi.trim());
            const response = await axiosInstance.get(`/api/globe/vessel-full-info/?${params.toString()}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching vessel full info:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Check NavAPI token status and remaining tokens.
     * @param {string} product - 'ais' or 'data'
     * @returns {Promise<Object>} Token status
     */
    getApiStatus: async (product = 'ais') => {
        try {
            const response = await axiosInstance.get(`/api/globe/api-status/?product=${product}`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching API status:', error);
            throw _normalizeError(error);
        }
    },

    // ─── Vessel Watchlist (per-user, dashboard-level) ────────────────

    /**
     * Get the current user's watchlist vessels.
     * @returns {Promise<Array>} Array of {id, imo, mmsi, vessel_name, ship_type, flag, created_at}
     */
    getWatchlist: async () => {
        try {
            const response = await axiosInstance.get('/api/globe/watchlist/');
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching watchlist:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Add a vessel to the current user's watchlist.
     * Server dedupes by (user, imo) and (user, mmsi).
     * @param {Object} vessel - { imo?, mmsi?, vessel_name, ship_type?, flag? }
     * @returns {Promise<{entry: Object, isDuplicate: boolean}>} The entry plus
     *   a flag indicating whether the server returned an existing record (200)
     *   vs. created a new one (201).
     */
    addWatchlistVessel: async (vessel) => {
        try {
            const body = {
                imo: vessel.imo || '',
                mmsi: vessel.mmsi || '',
                vessel_name: vessel.vessel_name || vessel.ship_name || '',
                ship_type: vessel.ship_type || '',
                flag: vessel.flag || '',
                loading_port_location: vessel.loading_port_location || '',
                discharging_port_location: vessel.discharging_port_location || '',
            };
            const response = await axiosInstance.post('/api/globe/watchlist/', body);
            return {
                entry: response.data.data,
                isDuplicate: response.status === 200,
            };
        } catch (error) {
            console.error('Error adding watchlist vessel:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Remove a vessel from the current user's watchlist.
     * @param {number} entryId - Watchlist entry ID
     * @returns {Promise<boolean>} true on success
     */
    removeWatchlistVessel: async (entryId) => {
        try {
            await axiosInstance.delete(`/api/globe/watchlist/${entryId}/`);
            return true;
        } catch (error) {
            console.error('Error removing watchlist vessel:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Update the load / discharge ports on a watchlist entry so its route can be
     * plotted on the map like a delivery vessel. Send only the keys you want to
     * change; '' clears a port. Ports are stored as the delivery-style canonical
     * "Port Name, Country, UNLOCODE" string.
     * @param {number} entryId - Watchlist entry ID
     * @param {Object} patch - { loading_port_location?, discharging_port_location? }
     * @returns {Promise<Object>} The updated watchlist entry
     */
    updateWatchlistVessel: async (entryId, patch) => {
        try {
            const response = await axiosInstance.patch(`/api/globe/watchlist/${entryId}/`, patch || {});
            return response.data.data;
        } catch (error) {
            console.error('Error updating watchlist vessel:', error);
            throw _normalizeError(error);
        }
    },

    /**
     * Batch sanctions/compliance screening for many hulls in one call, used to
     * colour fleet markers (green = cleared / unscreened, red = sanctioned or not
     * cleared). Reuses the same 6h VesselComplianceSnapshot cache as
     * getVesselCompliance, so it does not re-screen recently-checked vessels.
     * Non-fatal by design: any failure (incl. screening not configured, or the
     * caller lacking access) resolves to an empty result so marker colouring
     * simply falls back to all-green rather than erroring the whole fleet load.
     * @param {string[]} imos - IMO numbers
     * @returns {Promise<Object>} { configured, results: { [imo]: { sanctions_status, warning } } }
     */
    getVesselComplianceBatch: async (imos) => {
        const list = (imos || []).map(v => (v || '').toString().trim()).filter(Boolean);
        if (list.length === 0) return { configured: false, results: {} };
        try {
            const response = await axiosInstance.post('/api/globe/vessel-compliance-batch/', { imos: list });
            return response.data || { configured: false, results: {} };
        } catch (error) {
            console.error('Error fetching batch vessel compliance:', error);
            return { configured: false, results: {} };
        }
    },
};

/**
 * A readable message for a failed vessel/port request, keeping the server's
 * answer (status, body, original error) so a screen can still tell "not found"
 * from "not allowed" from a crash. Ledger row 128: this used to build a bare
 * `new Error(message)` and the answer was lost.
 */
function _normalizeError(error) {
    // Already rewritten here (getFleetPositions re-normalizes what
    // getFleetPositionsDetailed threw): hand it on unchanged, as before.
    if (error.response && error.cause?.response === error.response) return error;
    if (error.response?.data?.error) {
        return keepServerAnswer(error, error.response.data.error);
    }
    if (error.response?.status === 404) return keepServerAnswer(error, 'Vessel not found');
    if (error.response?.status === 401 || error.response?.status === 403) return keepServerAnswer(error, 'Authorization failed');
    if (error.response?.status >= 500) return keepServerAnswer(error, 'Vessel tracking service is currently unavailable');
    return error;
}
